import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { capitalCase } from 'capital-case';
import { MobileDatePicker } from '@mui/lab';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CaseStatus } from 'src/crs/case/dto/case.dto';
import { UpdateCaseDto } from 'src/crs/case/dto/update-case-dto';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import CancelButton from 'src/careflow/common/CancelButton';
import { CaseService } from 'src/crs/case/service/CaseService';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';

type FormValue = {
  number: string;
  create: string;
  status: string;
  owner: string;
  scope: string;
  step: number;
};

type Props = {
  data: any | null;
  open: boolean;
  onClose: VoidFunction;
  handleRefresh: VoidFunction;
  i18n: Function;
};

export default function Edit({ data, open, onClose, handleRefresh, i18n }: Props) {
  console.log('data', data);
  const { enqueueSnackbar } = useSnackbar();
  const [allUsers, setAllUsers] = useState([]);
  const [workflows, setWorkflows] = useState<any>(null);
  const [workflow, setWorkflow] = useState<any>(null);
  const [transitionList, setTransitionList] = useState<any>([]);
  const authUser = useAuth().getCurrentUser();

  const userOrganizations = useMemo(() => authUser?.organizations ?? [], [authUser?.organizations]);

  const orgsKey = useMemo(() => JSON.stringify(userOrganizations), [userOrganizations]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open]);

  useEffect(() => {
    fetchTransitions();
  }, [workflow]);

  useEffect(() => {
    fetchWorkFlow();
  }, [data]);

  useEffect(() => {
    if (open && workflow && orgsKey && orgsKey.length > 0) {
      getAllUsers();
    }
  }, [open, workflow, orgsKey]);

  const getAllUsers = async () => {
    if (!workflow) {
      return;
    }

    try {
      let workflowMetadata: any = null;
      let ownerACLs: string[] | null = null;

      if (workflow?.metadata) {
        try {
          workflowMetadata = JSON.parse(workflow.metadata);
          ownerACLs = workflowMetadata?.workflow?.ownerAllowedACLs;
        } catch (parseError) {
          console.log('error parsing workflow metadata', parseError);
        }
      }

      let result;
      if (ownerACLs && Array.isArray(ownerACLs) && ownerACLs.length > 0) {
        result = await UserService.getAllUsersByAcls(ownerACLs);
      } else {
        result = await UserService.getUsersByOrgAndRole(userOrganizations, []);
      }

      setAllUsers(result.filter((e: any) => e.status === 'Active'));
    } catch (err) {
      console.log('error on fetching all users', err);
    }
  };

  const fetchTransitions = async () => {
    const getTransitions = await WorkflowService.getTransitions(workflow?.id);
    setTransitionList(getTransitions);
  };

  const fetchWorkFlow = async () => {
    const getWorkflow = await WorkflowService.getWorkflowByType('case');
    const workflowByCode = getWorkflow?.find((e) => e?.code === data?.scope);
    setWorkflow(workflowByCode);
    workflows(getWorkflow);
  };

  const defaultValues = useMemo(
    () =>
      ({
        number: data?.internalNumber || '',
        create: data?.createdOnFormatted || '',
        status: data?.statusIndexed || '',
        owner: data?.owner || '',
        scope: data?.scope || '',
        step: data?.stepId || '',
      } as FormValue),
    [data]
  );

  const methods = useForm({ defaultValues });

  const { reset, control, handleSubmit } = methods;

  const onSubmit = async (dataForm: FormValue) => {
    const { status, owner, scope } = dataForm;
    try {
      if (data?.scope) {
        await updateCustomCase({
          status: status as CaseStatus,
          scope: data?.scope ?? scope,
          assignedTo: owner,
        });
      }

      if (data.step !== dataForm.step) {
        await updateStep(dataForm.step);
      }

      handleRefresh();
      enqueueSnackbar(`${data?.scope} was updated.`);
      handleClose();
    } catch (e) {
      enqueueSnackbar(`${data?.scope} was not updated.`, { variant: 'error' });
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const updateCustomCase = async (dataForm: UpdateCaseDto) => {
    const { id } = data;
    const update = CaseService.update(id, dataForm);
    return update;
  };

  const updateStep = async (idStepForm: any) => {
    try {
      const getTransitionId = transitionList.filter(
        (i: any) => Number(i.fromStagesId) === Number(idStepForm) && i.name === 'Next'
      );

      await WorkflowService.updateStage({
        reference_name: workflow?.referenceName,
        reference_value: data?.id,
        record: { id: 1 },
        stage: { id: idStepForm },
        workflow: { id: workflow?.id },
        transition: { id: getTransitionId?.[0]?.id },
      });
    } catch (error) {
      console.log('error updateStep', error);
    }
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>Edit {capitalCase(data?.scope || '')}</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ m: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <Typography variant="body2">Number</Typography>
                <RHFTextField disabled name="number" />

                <Typography variant="body2">Created</Typography>
                <Controller
                  name="create"
                  control={control}
                  render={({ field }) => (
                    <MobileDatePicker
                      disabled
                      {...field}
                      label=""
                      inputFormat="MMM dd, yyyy hh:mm"
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  )}
                />

                <Typography variant="body2">Status</Typography>
                <RHFSelect name="status" label="">
                  <MenuItem disabled />
                  {Object.entries(CaseStatus).map((option) => (
                    <MenuItem key={option[0]} value={option[1]}>
                      {capitalCase(option[1].replace('.', ' '))}
                    </MenuItem>
                  ))}
                </RHFSelect>

                <Typography variant="body2">Owner</Typography>
                <RHFSelect name="owner" label="">
                  <MenuItem disabled />
                  {allUsers?.map((option: any) => (
                    <MenuItem key={option.uuid} value={option.userName}>
                      {`${option.firstName} ${option.lastName}`}
                    </MenuItem>
                  ))}
                </RHFSelect>

                <Typography variant="body2">Scope</Typography>
                <RHFSelect name="scope" label="">
                  <MenuItem disabled />
                  {[
                    { id: 1, value: 'CovidCare' },
                    { id: 2, value: 'RPM' },
                  ]?.map((option: any) => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.value}
                    </MenuItem>
                  ))}
                </RHFSelect>

                <Typography variant="body2">Step</Typography>
                <RHFSelect name="step" label="">
                  <MenuItem disabled />
                  {workflow?.stages?.map((option: any) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Stack>
            </Grid>
          </Grid>
          <Stack spacing={2} alignItems="center">
            <DialogActions>
              <Box sx={{ flexGrow: 1 }} />

              <CancelButton onClick={handleClose}>Cancel</CancelButton>

              <Button variant="contained" color="info" type="submit">
                Submit
              </Button>
            </DialogActions>
          </Stack>
        </Card>
      </FormProvider>
    </Dialog>
  );
}
